import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  threadService,
  type CreateThreadData,
} from "../../services/threadService";
import { logger } from "../../utils/logger";
import { errorMessage } from "@jkast90/kast-common/format";
import type { Thread } from "../../types/entities";
import type { RootState } from "../store";

// Type definitions
export type { Thread };

/** A thread to save: its title and image. */
export type ThreadData = CreateThreadData;

export interface ThreadState {
  threads: Thread[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheValid: boolean;
}

interface CreateThreadParams {
  threadData: ThreadData;
  userEmail: string;
}

interface UpdateThreadParams {
  threadId: number;
  updateData: Partial<ThreadData>;
}

// Async thunks
export const loadThreads = createAsyncThunk<
  Thread[],
  string | null,
  { rejectValue: string | undefined }
>("thread/loadThreads", async (userEmail, { rejectWithValue }) => {
  if (!userEmail) {
    return rejectWithValue("No user email provided");
  }

  try {
    const threads = await threadService.getThreads(userEmail);
    logger.debug(`Loaded ${threads.length} threads for user:`, userEmail);
    return threads;
  } catch (error) {
    logger.error("Failed to load threads:", error);
    return rejectWithValue(errorMessage(error));
  }
});

export const createThread = createAsyncThunk<
  Thread,
  CreateThreadParams,
  { rejectValue: string | undefined }
>(
  "thread/createThread",
  async ({ threadData, userEmail }, { rejectWithValue }) => {
    if (!userEmail) {
      return rejectWithValue("Authentication required");
    }

    try {
      const savedThread = await threadService.createThread(
        threadData,
        userEmail,
      );
      logger.debug("Created thread:", {
        title: threadData.title,
        id: savedThread.id,
      });
      return savedThread;
    } catch (error) {
      logger.error("Thread creation failed:", error);
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const updateThread = createAsyncThunk<
  Thread,
  UpdateThreadParams,
  { rejectValue: string | undefined }
>(
  "thread/updateThread",
  async ({ threadId, updateData }, { rejectWithValue }) => {
    try {
      const updatedThread = await threadService.updateThread(
        threadId,
        updateData,
      );
      logger.debug("Updated thread:", { threadId, updateData });
      return updatedThread;
    } catch (error) {
      logger.error("Thread update failed:", error);
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const deleteThread = createAsyncThunk<
  number,
  number,
  { rejectValue: string | undefined }
>("thread/deleteThread", async (threadId, { rejectWithValue }) => {
  try {
    await threadService.deleteThread(threadId);
    logger.debug("Deleted thread:", threadId);
    return threadId;
  } catch (error) {
    logger.error("Thread deletion failed:", error);
    return rejectWithValue(errorMessage(error));
  }
});

const initialState: ThreadState = {
  threads: [],
  loading: false,
  saving: false,
  error: null,
  lastFetched: null,
  cacheValid: false,
};

const threadSlice = createSlice({
  name: "thread",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearThreads: (state) => {
      state.threads = [];
      state.lastFetched = null;
      state.cacheValid = false;
    },
    setCacheValidity: (state, action: PayloadAction<boolean>) => {
      state.cacheValid = action.payload;
      if (!action.payload) {
        state.lastFetched = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load threads
      .addCase(loadThreads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadThreads.fulfilled, (state, action) => {
        state.loading = false;
        state.threads = action.payload;
        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadThreads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      // Create thread
      .addCase(createThread.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        state.saving = false;
        state.threads.unshift(action.payload); // Add to beginning of array
      })
      .addCase(createThread.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || null;
      })

      // Update thread
      .addCase(updateThread.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateThread.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.threads.findIndex(
          (thread) => thread.id === action.payload.id,
        );
        if (index !== -1) {
          state.threads[index] = action.payload;
        }
      })
      .addCase(updateThread.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || action.error?.message || null;
      })

      // Delete thread
      .addCase(deleteThread.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteThread.fulfilled, (state, action) => {
        state.loading = false;
        state.threads = state.threads.filter(
          (thread) => thread.id !== action.payload,
        );
      })
      .addCase(deleteThread.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || null;
      });
  },
});

export const { clearError, clearThreads, setCacheValidity } =
  threadSlice.actions;

// Selectors
export const selectThreads = (state: RootState) => state.thread.threads;
export const selectThreadsLoading = (state: RootState) => state.thread.loading;
export const selectThreadsError = (state: RootState) => state.thread.error;

// Cache selectors
export const selectThreadsCacheValid = (state: RootState) =>
  state.thread.cacheValid;
export const selectThreadsLastFetched = (state: RootState) =>
  state.thread.lastFetched;

// Cache TTL - 5 minutes
const THREADS_CACHE_TTL = 5 * 60 * 1000;

export const selectThreadsCacheExpired = (state: RootState) => {
  const lastFetched = state.thread.lastFetched;
  if (!lastFetched) return true;
  return Date.now() - lastFetched > THREADS_CACHE_TTL;
};

// Helper selector to determine if we should fetch threads
export const selectShouldFetchThreads = (state: RootState) => {
  const hasThreads = state.thread.threads.length > 0;
  const cacheExpired = selectThreadsCacheExpired(state);
  return !hasThreads || cacheExpired;
};

export default threadSlice.reducer;
